import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { robotsFor } from './seo/policy';
import { Container } from 'react-bootstrap';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Header from './Components/Header';
import Footer from './Components/Footer';
import AdminOrderNotifier from './Components/AdminOrderNotifier';

import { useDispatch, useSelector } from 'react-redux';
import { setCartOwner } from './slices/cartSlice';

// 🔽 PayPal root provider + query for client id
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useGetPayPalClientIdQuery } from './slices/ordersApiSlice';

const App = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((s) => s.auth || {});
  const navigate = useNavigate();
  const location = useLocation();

  // Keep a short in-tab location history so BackButton can recover sensible
  // previous routes when browser history is limited (e.g., opened in a new tab).
  const LOCATION_HISTORY_KEY = 'app:locationHistory';
  const MAX_LOCATION_HISTORY = 5;

  const buildLocationEntry = (loc) => ({
    pathname: loc.pathname,
    search: loc.search,
    hash: loc.hash,
    state: loc.state,
    key: loc.key,
  });

  const isSamePath = (a, b) =>
    !!a &&
    !!b &&
    a.pathname === b.pathname &&
    (a.search || '') === (b.search || '') &&
    (a.hash || '') === (b.hash || '');

  useEffect(() => {
    const next = buildLocationEntry(location);
    try {
      const raw = sessionStorage.getItem(LOCATION_HISTORY_KEY);
      const history = raw ? JSON.parse(raw) : [];
      const safeHistory = Array.isArray(history) ? history : [];
      const last = safeHistory[safeHistory.length - 1];
      if (!isSamePath(last, next)) {
        safeHistory.push(next);
      } else {
        safeHistory[safeHistory.length - 1] = next;
      }

      const trimmed = safeHistory.slice(-MAX_LOCATION_HISTORY);
      sessionStorage.setItem(LOCATION_HISTORY_KEY, JSON.stringify(trimmed));

      // Backward compatibility for components still reading a single fallback entry.
      const previous = trimmed.length > 1 ? trimmed[trimmed.length - 2] : null;
      if (previous) {
        sessionStorage.setItem('app:lastLocation', JSON.stringify(previous));
      }
    } catch (_) {}
  }, [location]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  // Keep cart owner aligned with current auth user (or 'guest' when logged out)
  useEffect(() => {
    dispatch(setCartOwner(userInfo?._id || 'guest'));
  }, [dispatch, userInfo]);

  // ✅ Refresh current route whenever garage changes (add/update/remove/select/clear)
  useEffect(() => {
    const onGarageChanged = () => navigate(0);
    window.addEventListener('garage:changed', onGarageChanged);
    return () => window.removeEventListener('garage:changed', onGarageChanged);
  }, [navigate]);

  // Fetch PayPal client id once; mount SDK at the app root
  const { data: paypal, isLoading: loadingPaypal } = useGetPayPalClientIdQuery();
  const clientId = paypal?.clientId || '';

  // Always provide the PayPal context so usePayPalScriptReducer works in any
  // child component (e.g. OrderScreen) regardless of whether the clientId has
  // loaded yet. When clientId isn't available we use deferLoading:true so the
  // SDK script isn't fetched; OrderScreen dispatches resetOptions + setLoadingStatus
  // with the real clientId once its own query completes.
  const paypalOpts = clientId
    ? { 'client-id': clientId, currency: 'CAD' }
    : { 'client-id': 'placeholder', currency: 'CAD', deferLoading: true };

  const Content = (
    <Container fluid className="px-0">
      <div className="page-shell">
        <PayPalScriptProvider options={paypalOpts}>
          <Outlet />
        </PayPalScriptProvider>
      </div>
    </Container>
  );

  return (
    <>
      <Helmet><meta name="robots" content={robotsFor(`${location.pathname}${location.search}`)} /></Helmet>
      <Header />
      {/* Admin new-order popup + sound */}
      <AdminOrderNotifier />
      <main className="py-3">{Content}</main>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default App;
